<template>
  <div class="table-wrapper">
    <!-- Table header -->
    <div class="customer-list-header">
      <div class="customer-table-row header-row">
        <div class="col-name"><span class="vtext-subtitle">Name</span></div>
        <div class="col-email desktop-only"><span class="vtext-subtitle">Email</span></div>
        <div class="col-mobile desktop-only"><span class="vtext-subtitle">Mobile Number</span></div>
        <div class="col-interaction desktop-only"><span class="vtext-subtitle">Last Interaction</span></div>
        <div class="col-optin desktop-only"><span class="vtext-subtitle">Opt-in Status</span></div>
        <div class="col-actions desktop-only text-right"><span class="vtext-subtitle">Actions</span></div>
      </div>
    </div>

    <!-- Skeleton rows while loading -->
    <div v-if="loading" class="customer-info">
      <div
        v-for="n in 8"
        :key="n"
        class="customer-table-row rowBorder"
      >
        <div class="col-name">
          <div class="name-cell">
            <div class="custMargin">
              <span class="skeleton-text skeleton-circle" style="width: 25px; height: 25px;"></span>
            </div>
            <span class="skeleton-text" style="width: 120px; height: 14px;"></span>
          </div>
        </div>
        <div class="col-email desktop-only">
          <span class="skeleton-text" style="width: 110px; height: 14px;"></span>
        </div>
        <div class="col-mobile desktop-only">
          <span class="skeleton-text" style="width: 100px; height: 14px;"></span>
        </div>
        <div class="col-interaction desktop-only">
          <span class="skeleton-text" style="width: 100px; height: 14px;"></span>
        </div>
        <div class="col-optin desktop-only">
          <span class="skeleton-text" style="width: 80px; height: 28px; border-radius: 20px;"></span>
        </div>
        <div class="col-actions desktop-only text-right">
          <span class="skeleton-text" style="width: 80px; height: 32px; margin-left: auto;"></span>
        </div>
      </div>
    </div>

    <!-- Customer rows -->
    <div v-if="!loading" class="customer-info">
      <div
        v-for="customer in customers"
        :key="customer.id"
        @click="customer.count = 0"
        class="customer-table-row rowBorder"
      >
        <!-- Name -->
        <div class="col-name">
          <div class="name-cell">
            <div class="custMargin">
              <CustomerConnectedChannel :customer="customer" />
            </div>
            <div class="flex-start-center">
              <h2 v-if="customer.name && customer.name !== 'unrecorded'" class="customerName" :title="customer.name">
                {{ customer.name }}
              </h2>
              <h2 v-else class="customerName" title="Anonymous">Anonymous</h2>
              <HelpTooltip
                v-if="isDisconnectedChannel(customer)"
                :iconImage="conversationDeletedIcon"
              >
                Conversation's Channel has been disconnected
              </HelpTooltip>
            </div>
          </div>
        </div>

        <!-- Email -->
        <div class="col-email desktop-only">
          <p v-if="customer.email && !['unrecorded', 'valid', 'None'].includes(customer.email)" class="customerInfo">
            {{ customer.email }}
          </p>
          <p v-else class="customerInfo">Email not captured</p>
        </div>

        <!-- Mobile -->
        <div class="col-mobile desktop-only">
          <p class="customerInfo">
            {{ customer.phone == null ? 'Not captured' : `${customer.mobilecountrycode} ${customer.phone}` }}
          </p>
        </div>

        <!-- Last interaction -->
        <div class="col-interaction desktop-only">
          <span class="customerInfo">
            {{ renderDateTime_lastReplyTimestamp(customer.lastReplyTimestamp, 'customReadable', false) }}
          </span>
        </div>

        <!-- Opt-in status -->
        <div class="col-optin desktop-only">
          <span class="optin-status-tag optin-nostatus" v-if="customer.marketing_opt_in_state === 0">
            <img src="@/resources/images/marketing-optin-nostatus.svg" alt="no_status" />
            No status
          </span>
          <span class="optin-status-tag optin-subscribed" v-if="customer.marketing_opt_in_state === 1">
            <img src="@/resources/images/marketing-optin-subscribed.svg" alt="subscribed" />
            Subscribed
          </span>
          <span class="optin-status-tag optin-unsubscribed" v-if="customer.marketing_opt_in_state === -1">
            <img src="@/resources/images/marketing-optin-unsubscribed.svg" alt="unsubscribed" />
            Unsubscribed
          </span>
        </div>

        <!-- Desktop actions -->
        <div class="col-actions desktop-only">
          <div class="actionFlexContainer">
            <span class="tooltip_container">
              <img :src="isDarkMode ? crmDetailsDark : crmDetails" class="channelIcon cursorPointer" alt="details_icon" @click="$emit('open-detail', customer)" />
              <span class="tooltip_text" style="width: 86px">View / Edit details</span>
            </span>
            <span class="tooltip_container">
              <VIcon name="message-line" :size="-5" class="actionIcons cursorPointer" @click="$emit('view-conversation', customer.conversationid)" />
              <span class="tooltip_text" style="width: 90px">View Conversation</span>
            </span>
            <span v-if="isAdmin" class="tooltip_container">
              <VIcon name="delete-line" :size="-5" connotation="alert" class="trashIcon actionIcons cursorPointer" @click="$emit('delete-customer', customer.conversationid)" />
              <span class="tooltip_text" style="width: 90px">Delete</span>
            </span>
          </div>
        </div>

        <!-- Mobile actions -->
        <div class="col-mobile-actions mobile-only">
          <ActionPopOver :style="{ '--width': '215px' }">
            <div class="listContainer">
              <button class="popover-item" @click="$emit('open-detail', customer)">View Details</button>
              <button class="popover-item" @click="$emit('view-conversation', customer.conversationid)">View Conversation</button>
              <button v-if="isAdmin" class="popover-item popover-item--danger" @click="$emit('delete-customer', customer.conversationid)">Delete</button>
            </div>
          </ActionPopOver>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ActionPopOver from '@/components/ActionPopOver.vue';
import CustomerConnectedChannel from '@/components/customerConnectedChannel.vue';
import HelpTooltip from '@/components/HelpTooltip.vue';
import crmDetailsDark from '@/resources/images/crm_details_dark.svg';
import crmDetails from '@/resources/images/crm_details.svg';
import conversationDeletedIcon from '@/resources/images/conversation_deleted.svg';
import { useSocialChannels } from '@/composables/useSocialChannels';
import { renderDateTime_lastReplyTimestamp } from '@/helpers/date.helpers';
import { useAuthStore } from '@/stores/auth';
import { useUIStore } from '@/stores/ui';

export default {
  name: 'CustomerListTable',
  setup() {
    const { whatsAppSocialData, smsSocialData, googlercsSocialData } = useSocialChannels();
    return { whatsAppSocialData, smsSocialData, googlercsSocialData };
  },
  components: { ActionPopOver, CustomerConnectedChannel, HelpTooltip },
  props: {
    customers: { type: Array, required: true },
    loading: { type: Boolean, default: false },
  },
  emits: ['open-detail', 'view-conversation', 'delete-customer'],
  data() {
    return {
      crmDetailsDark,
      crmDetails,
      conversationDeletedIcon,
    };
  },
  computed: {
    isAdmin() { return useAuthStore().isAdmin; },
    isDarkMode() { return useUIStore().isDarkMode; },
  },
  methods: {
    renderDateTime_lastReplyTimestamp,
    isDisconnectedChannel(customer) {
      const { platform, channel_id } = customer;
      if (platform === 'whatsapp' && this.whatsAppSocialData) {
        return String(channel_id) !== String(this.whatsAppSocialData.id);
      }
      if (platform === 'sms' && this.smsSocialData) {
        return String(channel_id) !== String(this.smsSocialData.id);
      }
      if (platform === 'googlercs' && this.googlercsSocialData) {
        return String(channel_id) !== String(this.googlercsSocialData.id);
      }
      return false;
    },
  },
};
</script>

<style scoped>
.table-wrapper {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  margin-bottom: 15px;
}
.customer-list-header {
  background: var(--white);
  padding: 20px;
  border-bottom: 1px solid var(--background-primary);
  border-radius: 5px 5px 0px 0px;
  position: sticky;
  top: 0;
  z-index: 1;
  flex-shrink: 0;
}
.customer-info {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  border-radius: 0px 0px 5px 5px;
  padding: 0 20px;
  background: var(--white);
}

/* CSS Grid table layout */
.customer-table-row {
  display: grid;
  grid-template-columns: 2fr 3fr 2fr 2fr 1fr 2fr;
  align-items: center;
  gap: 10px;
}
.col-name { min-width: 0; }
.col-email { min-width: 0; }
.col-mobile { min-width: 0; }
.col-interaction { min-width: 0; }
.col-optin { min-width: 0; }
.col-actions { min-width: 0; }
.col-mobile-actions { min-width: 0; }
.text-right { text-align: right; }

.name-cell {
  display: flex;
  align-items: center;
}
.flex-start-center {
  display: flex;
  align-items: center;
  gap: 6px;
}

.rowBorder {
  border-bottom: 1px solid var(--background-primary);
  padding-bottom: 15px;
  padding-top: 15px;
}
.rowBorder:last-child {
  border-bottom: none;
}
.custMargin {
  margin-right: 15px;
}
.customerName,
.customerInfo {
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 16px;
  color: var(--black);
  margin: 0;
}
.customerName {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 150px;
}
.channelIcon {
  width: 25px;
  height: 25px;
}

/* Popover (mobile actions) */
.listContainer {
  display: flex;
  flex-direction: column;
  padding: 8px 0;
}
.popover-item {
  background: none;
  border: none;
  font: inherit;
  font-size: 14px;
  padding: 10px 16px;
  text-align: left;
  cursor: pointer;
  color: var(--black);
  transition: background 0.2s;
}
.popover-item:hover {
  background: var(--background-primary);
}
.popover-item--danger {
  color: var(--orange);
}

/* Opt-in status tags */
.optin-status-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 10px;
  border-radius: 20px;
  font-size: 14px;
  white-space: nowrap;
}
.optin-nostatus {
  background-color: #ededed;
  color: #54575e;
}
.optin-subscribed {
  background-color: #eafdf2;
  color: #2dd36f;
}
.optin-unsubscribed {
  background-color: #fee3dd;
  color: #fa7554;
}
.optin-status-tag img {
  vertical-align: middle;
  height: 14px;
  width: auto;
}

/* Action icons */
.actionIcons {
  width: 20px;
  height: 20px;
}
.actionFlexContainer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
}

/* Tooltips */
.tooltip_container {
  position: relative;
  display: inline-block;
}
.tooltip_container .tooltip_text {
  visibility: hidden;
  font-size: 13px;
  background-color: var(--black);
  color: var(--white);
  pointer-events: none;
  opacity: 0;
  transition: all 0.2s ease-out;
  text-align: center;
  padding: 5px;
  line-height: 1.5;
  border-radius: 5px;
  position: absolute;
  z-index: 1;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
}
.tooltip_container:hover .tooltip_text {
  visibility: visible;
  opacity: 1;
}
.tooltip_container .tooltip_text::after {
  content: " ";
  position: absolute;
  top: 100%;
  left: 50%;
  margin-left: -5px;
  border-width: 5px;
  border-style: solid;
  border-color: var(--black) transparent transparent transparent;
}

/* Skeleton loading */
.skeleton-text {
  display: inline-block;
  background: linear-gradient(90deg, var(--background-primary) 25%, #e8e8e8 50%, var(--background-primary) 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 4px;
}
.skeleton-circle {
  border-radius: 50%;
}
@keyframes skeleton-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* Responsive */
.desktop-only {
  display: block;
}
.mobile-only {
  display: none;
}
@media only screen and (max-width: 760px) {
  .desktop-only {
    display: none !important;
  }
  .mobile-only {
    display: block;
  }
  .customer-table-row {
    grid-template-columns: 8fr 4fr;
  }
  .rowBorder {
    padding: 15px 0;
  }
}
</style>
